import axios from 'axios';

// Определяем URL API в зависимости от окружения
// В production используем /api (Nginx проксирует /api/ на backend)
// В development используем localhost
const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000');

const api = axios.create({
  baseURL: API_URL,
});

// Интерсептор для добавления токена к запросам
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Интерсептор для обработки ошибок авторизации
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Отказ входа из Axion — не «сессия истекла»: причину покажет экран входа, а
    // редирект здесь стёр бы её до того, как человек её увидит.
    const ssoLogin = (error.config?.url || '').includes('/auth/axion-sso');
    if (error.response?.status === 401 && !ssoLogin) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const auth = {
  login: async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    const response = await api.post('/token', formData);
    return response.data;
  },
  // Вход с плитки «Склад» в Axion: одноразовый талон из ?sso= меняем на токен склада.
  loginFromAxion: async (ticket) => {
    const response = await api.post('/auth/axion-sso', { ticket });
    return response.data;
  },
  logout: async () => {
    try {
      await api.post('/logout');
    } catch {
      // токен мог истечь — всё равно чистим локально
    }
  },
  getCurrentUser: async () => {
    const response = await api.get('/users/me');
    return response.data;
  },
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/users/me/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },
  updateProfile: async ({ fullName, email }) => {
    const payload = {};
    if (fullName !== undefined) payload.full_name = fullName;
    if (email !== undefined) payload.email = email;
    const response = await api.patch('/users/me', payload);
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    return response.data.user;
  },
};

export const equipment = {
  getAll: async (filters = {}) => {
    const response = await api.get('/equipment', { params: filters });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/equipment/${id}`);
    return response.data;
  },
  getVendors: async () => {
    const response = await api.get('/vendors');
    return response.data;
  },
  getWarehouses: async () => {
    const response = await api.get('/warehouses');
    return response.data;
  },
  // Типы оборудования: { types: [{name, count}] — непустые, all: [весь закрытый список] }
  getTypes: async (filters = {}) => {
    const response = await api.get('/equipment-types', { params: filters });
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/equipment/${id}`, data);
    return response.data;
  },
};

export const partnerStock = {
  getAll: async (filters = {}) => {
    const response = await api.get('/partner-stock', { params: filters });
    return response.data;
  },
  getPartners: async () => {
    const response = await api.get('/partner-stock/partners');
    return response.data;
  },
  getVendors: async () => {
    const response = await api.get('/partner-stock/vendors');
    return response.data;
  },
  getWarehouses: async () => {
    const response = await api.get('/partner-stock/warehouses');
    return response.data;
  },
  importExcel: async (file, replaceAll = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/partner-stock/import', formData, {
      params: { replace_all: replaceAll },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  clearAll: async (partner = '') => {
    const response = await api.delete('/partner-stock', {
      params: partner ? { partner } : {},
    });
    return response.data;
  },
  downloadTemplate: async () => {
    const response = await api.get('/partner-stock/template', { responseType: 'blob' });
    return response.data;
  },
};

export const bookings = {
  getAll: async (filters = {}) => {
    const response = await api.get('/bookings', { params: filters });
    return response.data;
  },
  create: async (bookingData) => {
    const response = await api.post('/bookings', bookingData);
    return response.data;
  },
  cancel: async (id) => {
    const response = await api.patch(`/bookings/${id}/cancel`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/bookings/${id}`, data);
    return response.data;
  },
};

export const stats = {
  get: async () => {
    const response = await api.get('/stats');
    return response.data;
  },
};

export const users = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  resetPassword: async (userId, newPassword) => {
    const response = await api.post(`/users/${userId}/reset-password`, {
      new_password: newPassword,
    });
    return response.data;
  },
  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
  syncBitrixIds: async () => {
    const response = await api.post('/admin/users/sync-bitrix-ids');
    return response.data;
  },
};

export const importEquipment = {
  importExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/import-equipment-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  reloadDatabase: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/reload-database', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  downloadTemplate: async () => {
    const response = await api.get('/import-template', {
      responseType: 'blob',
    });
    return response.data;
  },
};

export const importLogs = {
  getDbLogs: async (limit = 50) => {
    const response = await api.get('/import-logs', { params: { limit } });
    return response.data;
  },
  getImportChanges: async (logId) => {
    const response = await api.get(`/import-logs/${logId}/changes`);
    return response.data;
  },
  getCompareReports: async (limit = 30) => {
    const response = await api.get('/import-logs/compare-reports', { params: { limit } });
    return response.data;
  },
  getCompareReport: async (reportId) => {
    const response = await api.get(`/import-logs/compare-reports/${reportId}`);
    return response.data;
  },
  getSyncReports: async (limit = 30) => {
    const response = await api.get('/import-logs/sync-reports', { params: { limit } });
    return response.data;
  },
  getSyncReport: async (runId) => {
    const response = await api.get(`/import-logs/sync-reports/${runId}`);
    return response.data;
  },
  getFileLogs: async (limit = 30) => {
    const response = await api.get('/import-logs/file-logs', { params: { limit } });
    return response.data;
  },
  getFileLog: async (filename, tailLines = 500) => {
    const response = await api.get(`/import-logs/file-logs/${filename}`, {
      params: { tail_lines: tailLines },
    });
    return response.data;
  },
  getSessions: async (params = {}) => {
    const response = await api.get('/admin/user-sessions', { params });
    return response.data;
  },
  exportSessions: async (params = {}) => {
    const response = await api.get('/admin/user-sessions/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

export const notifications = {
  getConfig: async () => {
    const response = await api.get('/admin/notifications/config');
    return response.data;
  },
  getRules: async () => {
    const response = await api.get('/admin/notification-rules');
    return response.data;
  },
  updateRule: async (id, data) => {
    const response = await api.patch(`/admin/notification-rules/${id}`, data);
    return response.data;
  },
  getBrandManagers: async () => {
    const response = await api.get('/admin/vendor-brand-managers');
    return response.data;
  },
  createBrandManager: async (data) => {
    const response = await api.post('/admin/vendor-brand-managers', data);
    return response.data;
  },
  deleteBrandManager: async (id) => {
    const response = await api.delete(`/admin/vendor-brand-managers/${id}`);
    return response.data;
  },
  getDeliveries: async (limit = 50) => {
    const response = await api.get('/admin/notification-deliveries', { params: { limit } });
    return response.data;
  },
  sendTestEmail: async (toEmail) => {
    const response = await api.post('/admin/notifications/test-email', { to_email: toEmail });
    return response.data;
  },
  dispatchExpiring: async () => {
    const response = await api.post('/admin/notifications/dispatch-expiring');
    return response.data;
  },
};

export default api;

